import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../utils/api";

const LAYER_META = [
  { key: "phone_verified", label: "手机认证", tone: "text-blue-600 bg-blue-50" },
  { key: "real_name_verified", label: "实名认证", tone: "text-green-700 bg-green-50" },
  { key: "face_verified", label: "真人认证", tone: "text-purple-700 bg-purple-50" },
  { key: "profile_verified", label: "资料认证", tone: "text-orange-700 bg-orange-50" },
  { key: "platform_verified", label: "平台认证", tone: "text-amber-700 bg-amber-50" },
];

function normalizeVerification(input) {
  if (!input) {
    return {
      verification_level: 0,
      verification_level_label: "未认证",
      phone_verified: false,
      real_name_verified: false,
      face_verified: false,
      profile_verified: false,
      platform_verified: false,
      is_verified: false,
    };
  }
  return {
    verification_level: Number(input.verification_level || 0),
    verification_level_label: input.verification_level_label || "未认证",
    phone_verified: Boolean(input.phone_verified),
    real_name_verified: Boolean(input.real_name_verified),
    face_verified: Boolean(input.face_verified),
    profile_verified: Boolean(input.profile_verified),
    platform_verified: Boolean(input.platform_verified),
    is_verified: Boolean(
      input.is_verified ||
        input.real_name_verified ||
        input.face_verified ||
        input.platform_verified ||
        input.phone_verified
    ),
  };
}

function VerificationDetailModal({ open, onClose, verification, userName }) {
  if (!open) return null;
  const v = normalizeVerification(verification);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm p-6 space-y-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-gray-800">认证详情</h3>
            <p className="text-sm text-gray-500 mt-1">
              {userName ? `${userName} · ` : ""}
              {v.verification_level_label}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            关闭
          </button>
        </div>

        <ul className="space-y-2">
          {LAYER_META.map((layer) => {
            const done = Boolean(v[layer.key]);
            return (
              <li
                key={layer.key}
                className="flex items-center justify-between rounded-xl border px-3 py-2.5 text-sm"
              >
                <span className="text-gray-700">{layer.label}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    done ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {done ? "已完成" : "尚未完成"}
                </span>
              </li>
            );
          })}
        </ul>

        <p className="text-xs text-gray-400 leading-6">
          平台只展示认证状态，不会公开手机号、身份证号、人脸等敏感信息。
        </p>
      </div>
    </div>
  );
}

/**
 * 认证徽章
 * - variant="detail"：主页完整展示
 * - variant="compact"：列表缩略
 * - verification：可直接传入；否则用 userId 拉取公开接口
 */
export default function VerificationBadges({
  verification,
  userId,
  userName = "",
  variant = "detail",
  className = "",
}) {
  const [remote, setRemote] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (verification || !userId) return undefined;
    let cancelled = false;
    apiFetch(`/api/verification/public/${userId}`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && !data.detail) setRemote(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [verification, userId]);

  const v = useMemo(
    () => normalizeVerification(verification || remote),
    [verification, remote]
  );

  const chips = LAYER_META.filter((layer) => v[layer.key]);

  if (variant === "compact") {
    if (!v.is_verified && v.verification_level <= 0) {
      return (
        <span className={`text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 ${className}`}>
          未认证
        </span>
      );
    }
    return (
      <>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }}
          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100 ${className}`}
          title="查看认证详情"
        >
          {v.platform_verified
            ? "🏅 平台认证"
            : v.face_verified
            ? "🟣 真人"
            : v.real_name_verified
            ? "🟢 实名"
            : v.phone_verified
            ? "🔵 手机"
            : "已认证"}
        </button>
        <VerificationDetailModal
          open={open}
          onClose={() => setOpen(false)}
          verification={v}
          userName={userName}
        />
      </>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`text-xs px-3 py-1 rounded-full font-medium ${
            v.verification_level > 0
              ? "bg-pink-100 text-pink-600"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {v.verification_level_label}
        </button>
        {chips.map((layer) => (
          <button
            key={layer.key}
            type="button"
            onClick={() => setOpen(true)}
            className={`text-xs px-2.5 py-1 rounded-full ${layer.tone}`}
          >
            ✓ {layer.label.replace("认证", "")}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-gray-400 hover:text-pink-500"
      >
        查看认证详情
      </button>
      <VerificationDetailModal
        open={open}
        onClose={() => setOpen(false)}
        verification={v}
        userName={userName}
      />
    </div>
  );
}

export { normalizeVerification, VerificationDetailModal };
